import React from 'react';
import { Tag, Store } from 'lucide-react';
import { unusedIncentives } from '../utils/couponPresentation';

const formatCurrency = (value) =>
  typeof value === 'number' && Number.isFinite(value)
    ? `₹${Number.isInteger(value) ? value : value.toFixed(2)}`
    : null;

const isSplitQuote = (quote) =>
  quote?.fulfillmentMode === 'split' && Array.isArray(quote.fulfillments) && quote.fulfillments.length >= 2;

const OfferTag = ({ offer, couponCode }) => {
  if (!offer?.label) return null;
  return (
    <span className="flex items-center gap-1 text-xs font-semibold text-accent-700 mt-0.5">
      <Tag size={12} className="shrink-0" aria-hidden="true" />
      <span className="break-words">{offer.label}</span>
      {offer.code && offer.code === couponCode && (
        <span className="font-mono shrink-0">· {offer.code}</span>
      )}
    </span>
  );
};

const DeliveryFeeRow = ({ quote, deliveryOffer, couponCode }) => (
  <div className="flex justify-between gap-4 text-ink-secondary">
    <span className="min-w-0">
      <span className="block">Delivery Fee</span>
      {deliveryOffer?.label && (
        <span className="flex items-center gap-1 text-xs font-semibold text-accent-700 mt-0.5" data-testid="delivery-offer">
          <Tag size={12} className="shrink-0" aria-hidden="true" />
          <span className="break-words">{deliveryOffer.label}</span>
          {deliveryOffer.code && deliveryOffer.code === couponCode && (
            <span className="font-mono shrink-0">· {deliveryOffer.code}</span>
          )}
        </span>
      )}
    </span>
    <span className="shrink-0 flex items-baseline gap-2">
      {deliveryOffer && quote.isFreeDelivery && quote.deliveryFeeBeforeOffer > 0 && (
        <span className="text-sm text-gray-400 line-through">{formatCurrency(quote.deliveryFeeBeforeOffer)}</span>
      )}
      <span className={quote.isFreeDelivery ? 'font-semibold text-emerald-700' : ''}>
        {quote.isFreeDelivery ? 'Free' : formatCurrency(quote.deliveryFee)}
      </span>
    </span>
  </div>
);

const FulfillmentGroup = ({ fulfillment, index, total, couponCode, cartItems = [] }) => {
  const cartOffer = fulfillment.appliedCartOffer?.level !== 'delivery' ? fulfillment.appliedCartOffer : null;
  const deliveryOffer =
    fulfillment.appliedCartOffer?.level === 'delivery' && fulfillment.deliveryFeeWaivedByOffer
      ? fulfillment.appliedCartOffer : null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3.5" data-testid={`fulfillment-group-${index}`}>
      <div className="flex items-center gap-2 mb-3">
        <Store size={15} className="text-emerald-700 shrink-0" aria-hidden="true" />
        <span className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
          Order {index + 1} of {total}
        </span>
        <span className="mx-1 text-gray-300">·</span>
        <span className="text-xs text-ink-secondary truncate">{fulfillment.outlet?.name || 'Store'}</span>
      </div>

      <ul className="text-[13px] text-ink-secondary mb-2.5 space-y-1">
        {fulfillment.items?.map((item) => {
          const cartItem = cartItems.find((ci) => String(ci.productId) === String(item.productId));
          const name = item.productName || cartItem?.product?.displayName || cartItem?.product?.name || item.productId;
          return (
          <li key={item.productId} className="flex justify-between gap-3">
            <span className="truncate">{name} × {item.quantity}</span>
            <span className="shrink-0">{formatCurrency(item.lineTotal ?? (item.unitPrice * item.quantity - (item.discount || 0)))}</span>
          </li>
          );
        })}
      </ul>

      <div className="space-y-1.5 text-[13.5px] border-t border-gray-100 pt-2.5">
        <div className="flex justify-between gap-4 text-ink-secondary">
          <span>Subtotal</span>
          <span>{formatCurrency(fulfillment.subtotal)}</span>
        </div>
        {fulfillment.itemDiscountTotal > 0 && (
          <div className="flex justify-between gap-4 text-emerald-700">
            <span>Item Discount</span>
            <span>-{formatCurrency(fulfillment.itemDiscountTotal)}</span>
          </div>
        )}
        {fulfillment.cartDiscount > 0 && (
          <div className="flex justify-between gap-4 text-emerald-700">
            <span className="min-w-0">
              <span className="block">Cart Discount</span>
              <OfferTag offer={cartOffer} couponCode={couponCode} />
            </span>
            <span className="shrink-0">-{formatCurrency(fulfillment.cartDiscount)}</span>
          </div>
        )}
        <DeliveryFeeRow quote={fulfillment} deliveryOffer={deliveryOffer} couponCode={couponCode} />
        {fulfillment.handlingCharge > 0 && (
          <div className="flex justify-between gap-4 text-ink-secondary">
            <span>Handling Charge</span>
            <span>{formatCurrency(fulfillment.handlingCharge)}</span>
          </div>
        )}
        {fulfillment.platformCharge > 0 && (
          <div className="flex justify-between gap-4 text-ink-secondary">
            <span>Platform Fee</span>
            <span>{formatCurrency(fulfillment.platformCharge)}</span>
          </div>
        )}
        <div className="flex justify-between gap-4 font-semibold text-ink-primary pt-1">
          <span>Order Total</span>
          <span>{formatCurrency(fulfillment.grandTotal)}</span>
        </div>
      </div>
    </div>
  );
};

const QuoteSummary = ({ quote, itemCount, couponCode = null, cartItems = [] }) => {
  const appliedCartOffer = quote.appliedCartOffer || null;
  const deliveryOffer =
    appliedCartOffer?.level === 'delivery' && quote.deliveryFeeWaivedByOffer ? appliedCartOffer : null;
  const cartOffer = appliedCartOffer && appliedCartOffer.level !== 'delivery' ? appliedCartOffer : null;
  const notUsed = unusedIncentives(quote).filter(
    (entry) => !(entry.source === 'store_offer' && couponCode && entry.code === couponCode)
  );

  const split = isSplitQuote(quote);

  return (
    <>
      {split && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-4 text-[13.5px] text-amber-900" data-testid="split-banner">
          Your items come from {quote.fulfillments.length} stores. You pay once and get {quote.fulfillments.length} separate deliveries.
        </div>
      )}

      {split && (
        <div className="space-y-3 mb-5">
          {quote.fulfillments.map((f, i) => (
            <FulfillmentGroup
              key={f.outlet?.id || i}
              fulfillment={f}
              index={i}
              total={quote.fulfillments.length}
              couponCode={couponCode}
              cartItems={cartItems}
            />
          ))}
        </div>
      )}

      {!split && (
        <div className="space-y-3 text-[14.5px] mb-5">
          <div className="flex justify-between gap-4 text-ink-secondary">
            <span>Subtotal ({itemCount} item{itemCount === 1 ? '' : 's'})</span>
            <span>{formatCurrency(quote.subtotal)}</span>
          </div>
          {quote.itemDiscountTotal > 0 && (
            <div className="flex justify-between gap-4 text-emerald-700">
              <span>Item Discount</span>
              <span>-{formatCurrency(quote.itemDiscountTotal)}</span>
            </div>
          )}
          {quote.cartDiscount > 0 && (
            <div className="flex justify-between gap-4 text-emerald-700" data-testid="cart-offer">
              <span className="min-w-0">
                <span className="block">Cart Discount</span>
                <OfferTag offer={cartOffer} couponCode={couponCode} />
              </span>
              <span className="shrink-0">-{formatCurrency(quote.cartDiscount)}</span>
            </div>
          )}
          {quote.referralDiscount?.discountAmount > 0 && (
            <div className="flex justify-between gap-4 text-emerald-700">
              <span>Referral Discount</span>
              <span>-{formatCurrency(quote.referralDiscount.discountAmount)}</span>
            </div>
          )}
          <DeliveryFeeRow quote={quote} deliveryOffer={deliveryOffer} couponCode={couponCode} />
          <div className="flex justify-between gap-4 text-ink-secondary">
            <span>
              Tax <span className="text-xs">(included in prices)</span>
            </span>
            <span>{formatCurrency(quote.totalTax)}</span>
          </div>
          {quote.platformCharge > 0 && (
            <div className="flex justify-between gap-4 text-ink-secondary">
              <span>Platform Charge</span>
              <span>{formatCurrency(quote.platformCharge)}</span>
            </div>
          )}
          {quote.handlingCharge > 0 && (
            <div className="flex justify-between gap-4 text-ink-secondary">
              <span>Handling Charge</span>
              <span>{formatCurrency(quote.handlingCharge)}</span>
            </div>
          )}
        </div>
      )}

      {split && (
        <div className="space-y-3 text-[14.5px] mb-5 border-t border-gray-200 pt-4">
          <div className="flex justify-between gap-4 text-ink-secondary">
            <span>Subtotal ({itemCount} item{itemCount === 1 ? '' : 's'})</span>
            <span>{formatCurrency(quote.subtotal)}</span>
          </div>
          {quote.deliveryFee > 0 && (
            <div className="flex justify-between gap-4 text-ink-secondary">
              <span>Delivery ({quote.fulfillments.length} deliveries)</span>
              <span>{formatCurrency(quote.deliveryFee)}</span>
            </div>
          )}
          {quote.referralDiscount?.discountAmount > 0 && (
            <div className="flex justify-between gap-4 text-emerald-700">
              <span>Referral Discount</span>
              <span>-{formatCurrency(quote.referralDiscount.discountAmount)}</span>
            </div>
          )}
          {quote.totalTax > 0 && (
            <div className="flex justify-between gap-4 text-ink-secondary">
              <span>Tax <span className="text-xs">(included in prices)</span></span>
              <span>{formatCurrency(quote.totalTax)}</span>
            </div>
          )}
        </div>
      )}

      {notUsed.length > 0 && (
        <ul className="mb-5 space-y-1.5 text-xs text-ink-secondary" data-testid="unused-incentives">
          {notUsed.map((entry) => (
            <li key={entry.id || entry.label}>
              {entry.source === 'referral_reward'
                ? `${entry.label} wasn't used on this order, because the offers here save you more.`
                : `${entry.label} wasn't used, because your referral reward saves you more.`}
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between gap-4 rounded-2xl bg-emerald-50 border border-emerald-100 px-5 py-3.5 mb-5">
        <span className="text-lg xl:text-xl font-bold text-emerald-950">
          {split ? `Total for ${quote.fulfillments.length} orders` : 'Total Amount'}
        </span>
        <span className="text-2xl xl:text-[28px] font-bold text-emerald-800">{formatCurrency(quote.grandTotal)}</span>
      </div>
    </>
  );
};

export default QuoteSummary;
